/**
 * Certification Modal Component
 * Modal for managing driver certifications
 */

import { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useDriver, useAddCertification, useDeleteCertification } from '../../../hooks/useDrivers';
import { addCertificationSchema } from '../../../schemas/driverSchema';
import type { AddCertificationFormData } from '../../../schemas/driverSchema';

interface CertificationModalProps {
  driverId: string;
  open: boolean;
  onClose: () => void;
}

export function CertificationModal({ driverId, open, onClose }: CertificationModalProps) {
  const { data: driver } = useDriver(driverId);
  const { mutateAsync: addCert, isPending: isAdding } = useAddCertification();
  const { mutateAsync: deleteCert, isPending: isDeleting } = useDeleteCertification();
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<AddCertificationFormData>({
    certificationType: '',
    certificationNumber: '',
    issueDate: '',
    expirationDate: '',
  });

  useEffect(() => {
    if (open) {
      setShowForm(false);
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = addCertificationSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) newErrors[err.path[0] as string] = err.message;
      });
      setErrors(newErrors);
      return;
    }

    try {
      await addCert({ driverId, data: result.data });
      setShowForm(false);
      setFormData({
        certificationType: '',
        certificationNumber: '',
        issueDate: '',
        expirationDate: '',
      });
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to add certification' });
    }
  };

  const handleDelete = async (certId: string) => {
    if (confirm('Are you sure you want to delete this certification?')) {
      try {
        await deleteCert({ driverId, certificationId: certId });
      } catch (error) {
        console.error('Failed to delete certification:', error);
      }
    }
  };

  const updateField = (field: keyof AddCertificationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Driver Certifications</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Existing Certifications */}
          {driver && driver.certifications.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Current Certifications</h4>
              {driver.certifications.map((cert) => {
                const isExpired = cert.expirationDate && new Date(cert.expirationDate) < new Date();
                return (
                  <div key={cert.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium">{cert.certificationType}</div>
                        {cert.certificationNumber && (
                          <div className="text-sm text-gray-600">{cert.certificationNumber}</div>
                        )}
                        <div className="mt-1 text-xs text-gray-500">
                          Issued: {new Date(cert.issueDate).toLocaleDateString()}
                          {cert.expirationDate && (
                            <> • Expires: {new Date(cert.expirationDate).toLocaleDateString()}</>
                          )}
                        </div>
                        {isExpired && (
                          <Badge variant="destructive" className="mt-1">Expired</Badge>
                        )}
                      </div>
                      <button
                        onClick={() => handleDelete(cert.id)}
                        disabled={isDeleting}
                        className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Certification Form */}
          {!showForm ? (
            <Button
              onClick={() => setShowForm(true)}
              variant="outline"
              className="w-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Certification
            </Button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-medium text-gray-700">Add New Certification</h4>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Certification Type <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.certificationType}
                  onChange={(e) => updateField('certificationType', e.target.value)}
                  className={`mt-1 block w-full rounded-md border ${
                    errors.certificationType ? 'border-red-500' : 'border-gray-300'
                  } px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                  placeholder="OSHA 30-Hour, Hazmat, etc."
                />
                {errors.certificationType && <p className="mt-1 text-sm text-red-500">{errors.certificationType}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Certification Number</label>
                <input
                  type="text"
                  value={formData.certificationNumber}
                  onChange={(e) => updateField('certificationNumber', e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Issue Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.issueDate}
                    onChange={(e) => updateField('issueDate', e.target.value)}
                    className={`mt-1 block w-full rounded-md border ${
                      errors.issueDate ? 'border-red-500' : 'border-gray-300'
                    } px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                  />
                  {errors.issueDate && <p className="mt-1 text-sm text-red-500">{errors.issueDate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Expiration Date</label>
                  <input
                    type="date"
                    value={formData.expirationDate}
                    onChange={(e) => updateField('expirationDate', e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>

              {errors.submit && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{errors.submit}</div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  disabled={isAdding}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isAdding} className="flex-1">
                  {isAdding ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add'
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-2">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
